import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { CreditCard, Check, Loader2, AlertCircle, Sparkles, LogIn } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AuthForm } from '../auth/AuthForm';
interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  priceId: string;
  popular?: boolean;
  discount?: string;
  isSubscription?: boolean;
}
const packages: CreditPackage[] = [{
  id: 'starter',
  name: 'Starter Pack',
  credits: 5,
  price: 1.99,
  priceId: 'price_1SMcjLBaZtBtpc8wLhQnNYvZ'
}, {
  id: 'pro',
  name: 'Pro Pack',
  credits: 10,
  price: 4.75,
  priceId: 'price_1SMcjLBaZtBtpc8wdu17Twd2',
  popular: true,
  discount: 'Best Value'
}, {
  id: 'unlimited',
  name: 'Unlimited Plan',
  credits: -1,
  price: 6.75,
  priceId: 'price_1SMcjLBaZtBtpc8wVE8mHnJM',
  discount: 'Monthly',
  isSubscription: true
}];
interface CreditPurchaseProps {
  currentCredits?: number;
  onPurchaseComplete?: () => void;
}
export function CreditPurchase({
  currentCredits = 0,
  onPurchaseComplete
}: CreditPurchaseProps) {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGuestMode, setIsGuestMode] = useState(false);
  useEffect(() => {
    checkAuthStatus();
  }, []);
  const checkAuthStatus = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) {
      const guestMode = localStorage.getItem('guestMode') === 'true';
      setIsGuestMode(guestMode);
    }
  };
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setError(null);
      if (onPurchaseComplete) {
        onPurchaseComplete();
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('canceled') === 'true') {
      setError('Payment was canceled.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [onPurchaseComplete]);
  const handlePurchase = async (pkg: CreditPackage) => {
    setLoading(pkg.id);
    setError(null);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be logged in to purchase credits');
      }
      const response = await fetch('https://api.scribetok.com/api/payments/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          price_id: pkg.priceId,
          user_email: user.email
        })
      });
      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }
      const {
        url
      } = await response.json();
      window.location.href = url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };
  if (isGuestMode) {
    return <div className="space-y-6" data-id="element-700">
        <div className="text-center py-6" data-id="element-701">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4" data-id="element-702">
            <LogIn size={24} className="text-primary" data-id="element-703" />
          </div>
          <h3 className="text-xl font-semibold mb-2" data-id="element-704">
            Sign In to Purchase Credits
          </h3>
          <p className="text-muted-foreground" data-id="element-705">
            Create an account or sign in to buy credits and unlock comment
            analysis
          </p>
        </div>
        <AuthForm onLoginSuccess={() => {
        window.location.reload();
      }} data-id="element-706" />
      </div>;
  }
  return <div className="space-y-6" data-id="element-707">
      {error && <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3" data-id="element-708">
          <AlertCircle size={20} className="text-destructive flex-shrink-0 mt-0.5" data-id="element-709" />
          <p className="text-sm text-destructive" data-id="element-710">{error}</p>
        </div>}
      <div className="bg-secondary/20 p-4 rounded-lg" data-id="element-711">
        <div className="flex items-center justify-between mb-2" data-id="element-712">
          <h3 className="font-medium" data-id="element-713">Current Balance</h3>
          <div className="flex items-center gap-2" data-id="element-714">
            <Sparkles size={16} className="text-primary" data-id="element-715" />
            <span className="text-2xl font-bold" data-id="element-716">{currentCredits}</span>
            <span className="text-muted-foreground" data-id="element-717">credits</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground" data-id="element-718">
          Each credit covers analysis of 100 comments
        </p>
      </div>
      <div data-id="element-719">
        <h3 className="text-lg font-semibold mb-2" data-id="element-720">Purchase Credits</h3>
        <p className="text-sm text-muted-foreground mb-4" data-id="element-721">
          Choose a package that fits your needs
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-722">
        {packages.map(pkg => <Card key={pkg.id} className={pkg.popular ? 'border-primary shadow-lg' : ''} data-id="element-723">
            <CardContent className="p-6" data-id="element-724">
              {pkg.popular && <div className="bg-primary text-primary-foreground text-xs font-medium px-2 py-1 rounded-full inline-block mb-3" data-id="element-725">
                  {pkg.discount}
                </div>}
              <h4 className="font-semibold text-lg mb-2" data-id="element-726">{pkg.name}</h4>
              <div className="mb-4" data-id="element-727">
                <span className="text-3xl font-bold" data-id="element-728">${pkg.price}</span>
                {pkg.isSubscription && <span className="text-muted-foreground ml-2" data-id="element-729">/month</span>}
              </div>
              <ul className="space-y-2 mb-6" data-id="element-730">
                <li className="flex items-center text-sm" data-id="element-731">
                  <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-732" />
                  {pkg.credits === -1 ? 'Unlimited' : pkg.credits} credits
                </li>
                <li className="flex items-center text-sm" data-id="element-733">
                  <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-734" />
                  {pkg.credits === -1 ? 'Unlimited' : `~${pkg.credits * 100}`}{' '}
                  comments
                </li>
                <li className="flex items-center text-sm" data-id="element-735">
                  <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-736" />
                  {pkg.isSubscription ? 'Renews monthly' : 'Never expires'}
                </li>
              </ul>
              <Button onClick={() => handlePurchase(pkg)} disabled={loading !== null} className="w-full" variant={pkg.popular ? 'default' : 'outline'} data-id="element-737">
                {loading === pkg.id ? <>
                    <Loader2 size={16} className="mr-2 animate-spin" data-id="element-738" />
                    Processing...
                  </> : <>
                    <CreditCard size={16} className="mr-2" data-id="element-739" />
                    Buy Now
                  </>}
              </Button>
            </CardContent>
          </Card>)}
      </div>
      <div className="bg-secondary/20 p-4 rounded-lg" data-id="element-740">
        <h4 className="font-medium text-sm mb-2" data-id="element-741">💳 Secure Payment</h4>
        <p className="text-xs text-muted-foreground" data-id="element-742">
          All payments are processed securely through Stripe. We never store
          your credit card information.
        </p>
      </div>
    </div>;
}